import React, { useContext, useEffect, useRef, useState } from "react";
import proj4 from "proj4";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import highchartsMap from "highcharts/modules/map";
import mapDataEurope from "@highcharts/map-collection/custom/europe.geo.json";
// import mapDataEurope from "@highcharts/map-collection/custom/world-continents.geo.json";
import { eColors, countriesCords } from "../../global_vars";
import UserContext from "../../services/user/user";
import { countries } from "country-data";
import messages_de from "../../translations/de.json";
import messages_en from "../../translations/en.json";
import messages_es from "../../translations/es.json";
import messages_fr from "../../translations/fr.json";
import { EcAuthContext } from "@eclear-ag/ec-common-ui";

if (typeof window !== "undefined") {
  window.proj4 = window.proj4 || proj4;
}

highchartsMap(Highcharts);

// Map palettes aligned to colors_v2.css design tokens.
// Light: countries neutral-100 (#ebebeb), flows/origin future-tax navy ramp.
const lightChartColors = {
  inactive: "#ebebeb", // neutral-neutral-100
  borderColor: "#ffffff", // future-tax-future-tax-0
  buttonFill: "#ffffff", // future-tax-future-tax-0
  lineColor: "#213551", // future-tax-future-tax-100
  stops: [
    [0, "#ceddfc"], // future-tax-future-tax-20
    [0.25, "#6ca0e8"], // future-tax-future-tax-60
    [0.5, "#416290"], // future-tax-future-tax-80
    [0.75, "#324664"], // future-tax-future-tax-90
    [1, "#213551"] // future-tax-future-tax-100
  ]
};
// Dark: countries neutral-800 (#323232), flows future-tax-90 ramp.
const darkChartColors = {
  inactive: "#323232", // neutral-neutral-800
  borderColor: "#202020", // neutral-neutral-850
  buttonFill: "#202020", // neutral-neutral-850
  lineColor: "#9cbdf4", // future-tax-future-tax-40
  stops: [
    [0, "#324664"], // future-tax-future-tax-90
    [0.25, "#416290"], // future-tax-future-tax-80
    [0.5, "#5180bf"], // future-tax-future-tax-70
    [0.75, "#6ca0e8"], // future-tax-future-tax-60
    [1, "#9cbdf4"] // future-tax-future-tax-40
  ]
};

export default function EMapChart({ mapData, fromToData, taxData, type }) {
  const { currency, formatNumber } = useContext(UserContext);
  const { isDarkMode } = useContext(EcAuthContext);
  const [countryMax, setCountryMax] = useState(1);
  const [chartObj, setChartObj] = useState({});

  const [chartColors, setChartColors] = useState(lightChartColors);

  const { lang } = useContext(UserContext);

  const messages = {
    de: messages_de,
    en: messages_en,
    es: messages_es,
    fr: messages_fr
  };

  const getCountryMaxValue = (mapType) => {
    let max = 1;

    switch (mapType) {
      case "heat":
        mapData.forEach((country) => {
          if (country.total_revenue > max) {
            max = country.total_revenue;
          }
        });
        break;
      case "tax":
        taxData.forEach((country) => {
          if (country.total_VAT > max) {
            max = country.total_VAT;
          }
        });
        break;
      case "flow":
        max = 999999;
        break;
      default:
        break;
    }
    setCountryMax(max);
  };

  useEffect(() => {
    getCountryMaxValue(type);
  }, [mapData, taxData, type]);

  useEffect(() => {
    setChartColors(isDarkMode ? darkChartColors : lightChartColors);
  }, [isDarkMode]);

  const resetChartObj = () => {
    let newChartObj = chartObj;
    while (newChartObj.series.length > 0) {
      newChartObj.series[0].remove(true);
    }

    setChartObj(newChartObj);
  };

  useEffect(() => {
    if (chartObj && Object.keys(chartObj).length !== 0) {
      resetChartObj();
      if (type === "flow") {
        chartObj.addSeries({
          // base data for drwaing lines
          type: "mappoint",
          name: "",
          dataLabels: {
            enabled: false
          },
          marker: {
            enabled: false
          },
          tooltip: {
            enabled: false,
            useHTML: true,
            headerFormat: "",
            pointFormatter: function () {
              return `${this.name}`;
            }
          },
          data: formatData()
        });
        chartObj.addSeries({
          // base data for drwaing lines
          type: "map",
          name: "",
          color: chartColors.inactive,
          borderColor: chartColors.borderColor,
          borderWidth: 2,
          enableMouseTracking: false,
          // dataLabels: {
          //   enabled: false,
          // },
          marker: {
            enabled: false
          },
          tooltip: {
            enabled: false
          },
          dataLabels: {
            enabled: true,
            // useHTML: true,
            formatter() {
              if (this.point.code) {
                return `<img alt="country flag" src="https://hatscripts.github.io/circle-flags/flags/${
                  this.point.properties && this.point.properties["hc-key"]
                }.svg" style="height: 25px; width: 25px; border:4px solid #ebebeb; border-radius: 100px; ">`;
              }
            }
          },
          data: formatData()
        });
        fromToData.forEach((item) => {
          addPaths_noTooltip(
            item.country,
            item.heatMapReportDtos,
            `${item.country} ${messages[lang]["revenuesCountries"]}`
          );
        });
        fromToData.forEach((item) => {
          addPaths(
            item.country,
            item.heatMapReportDtos,
            `${item.country} ${messages[lang]["revenuesCountries"]}`
          );
        });
        // console.log("__ chartObj: ", chartObj.series);
      } else if (type === "heat") {
        addHeat();
      } else {
        addTax();
      }
    }
  });

  (function (H) {
    // function selectiveRoundedRect(x, y, w, h, r) {
    //   var xStart = x + w / 2 - r,
    //     yStart = y + h / 2 - r;
    //   return [
    //     ["M", xStart, yStart],
    //     // top side
    //     ["A", r, r, 0, 0, 0, xStart + 2 * r, yStart + 2 * r],
    //     // top right corner
    //     ["A", r, r, 0, 0, 0, xStart, yStart],
    //   ];
    // }
    H.SVGRenderer.prototype.symbols.topbutton = function (x, y, w, h, options) {
      var r = (options && options.r) || 0;
      return "M5.814 0.663h18s5 0 5 5v18s0 5 -5 5h-18s-5 0 -5 -5v-18s0 -5 5 -5";
      // return selectiveRoundedRect(x - 1, y - 1, w, h, r);
    };
    H.SVGRenderer.prototype.symbols.bottombutton = function (
      x,
      y,
      w,
      h,
      options
    ) {
      var r = (options && options.r) || 0;
      return "M5.814 0.663h18s5 0 5 5v18s0 5 -5 5h-18s-5 0 -5 -5v-18s0 -5 5 -5";
      // return selectiveRoundedRect(x - 1, y - 1, w, h, r);
    };
  })(Highcharts);

  const chartContainerRef = useRef(null);

  const mapOptions = {
    credits: {
      enabled: false
    },
    chart: {
      events: {
        load: function () {
          setTimeout(() => {
            this?.mapView?.setView([4000, 3000], -2.5, true, false);
          }, 30);
        }
      },
      animation: false,
      mapData: mapDataEurope,
      height: 450,
      margin: 0
    },
    title: {
      text: ""
    },
    legend: {
      enabled: false
    },

    mapNavigation: {
      useHTML: true,
      enabled: true,
      buttonOptions: {
        align: "right",
        verticalAlign: "bottom",
        theme: { fill: chartColors.buttonFill, r: 5 },
        style: {
          fontWeight: "500",
          fontFamily: "Inter",
          color: chartColors.lineColor
        }
      },
      buttons: {
        zoomIn: {
          y: -28,
          x: -24,
          onclick: function () {
            this.mapView.zoomBy(2);
          },
          theme: {
            "data-testid": "map_view_zoom_in"
          }
        },
        zoomOut: {
          y: 5,
          x: -24,
          onclick: function () {
            this.mapView.zoomBy(0.5);
          },
          theme: {
            "data-testid": "map_view_zoom_out"
          }
        }
      }
    },
    mapView: {
      zoom: -4,
      center: [4000, 3000]
    },
    plotOptions: {
      map: {
        allAreas: true,
        joinBy: ["iso-a2", "code"],
        dataLabels: {
          enabled: true,
          useHTML: true
        },
        mapData: mapDataEurope
      },
      series: {
        nullColor: chartColors.inactive,
        marker: {
          enabled: true,
          fillColor: "#FFFFFF",
          lineWidth: 2,
          lineColor: chartColors.lineColor
        },
        states: {
          hover: {
            // color: "#a4edba",
            borderColor: chartColors.lineColor
          },
          normal: {}
        }
      }
    },
    colorAxis: {
      stops: chartColors.stops,
      min: 1,
      max: countryMax
    },

    tooltip: {
      className: "map-tooltip",
      headerFormat: '<div class="rev-line-table">',
      footerFormat: "</div>",
      shared: true,
      shadow: false,
      useHTML: true,
      style: {
        fontFamily: "Inter",
        opacity: 1,
        padding: 0
      },
      borderWidth: 0,
      borderRadius: 14,
      // backgroundColor: "#ebebeb",
      backgroundColor: "rgba(0,0,0,0)"
    }
  };

  function pointsToPath(from, to, invertArc) {
    var centerPoint = [(from.x + to.x) / 2, (from.y + to.y) / 2];
    var slope = (to.x - from.x) / (to.y - from.y);
    var invSlope = -1 / slope;
    var distance = Math.sqrt(
      Math.pow(to.x - from.x, 2) + Math.pow(to.y - from.y, 2)
    );
    var arcPointX = 0;
    var arcPointY = 0;
    var offset = 0;
    var offsetCenter = 0;

    if (Math.abs(slope) > Math.abs(invSlope)) {
      offset = (invertArc ? -1 : 1) * -10 * Math.sqrt(distance);

      offsetCenter = [
        centerPoint[0] + offset * (0.5 / slope),
        centerPoint[1] + offset
      ];

      arcPointX = offsetCenter[0];
      arcPointY = offsetCenter[1];
    } else {
      //invSlope >= slope
      //then we should offset in the x direction (then calc. y-offset)
      offset = (invertArc ? -1 : 1) * 10 * Math.sqrt(distance);
      offsetCenter = [
        centerPoint[0] + offset,
        centerPoint[1] + offset * (0.5 / invSlope)
      ];

      arcPointX = offsetCenter[0];
      arcPointY = offsetCenter[1];
    }
    return (
      "M" +
      from.x +
      " " +
      from.y +
      "Q" +
      arcPointX +
      " " +
      arcPointY +
      " " +
      to.x +
      " " +
      to.y
    );
  }

  // function pointsToPath(from, to, invertArc) {
  //   const curve = 0.05,
  //     arcPointX = (from.x + to.x) / (invertArc ? 2 + curve : 2 - curve),
  //     arcPointY = (from.y + to.y) / (invertArc ? 2 + curve : 2 - curve);
  //   return [
  //     ["M", from.x, from.y],
  //     ["Q", arcPointX, arcPointY, to.x, to.y],
  //   ];
  // }

  function addPaths_noTooltip(from, toArray, title) {
    const fromPoint = chartObj.get(from);
    return chartObj.addSeries({
      id: "flow1",
      name: title || "",
      type: "mapline",
      enableMouseTracking: false,
      className: "path-without-tooltip",
      color: chartColors.lineColor,
      tooltip: {
        enabled: false
      },
      data: toArray
        .map((to) => {
          return chartObj.get(to.country) && fromPoint
            ? {
                // id: `Thi is the Data: ${from} -> ${to.toCountry}`,
                path: pointsToPath(fromPoint, chartObj.get(to.country), true),
                lineWidth: 5
              }
            : undefined;
        })
        .filter((item) => item !== undefined)
    });
  }

  function addPaths(from, toArray, title) {
    const fromPoint = chartObj.get(from);
    return chartObj.addSeries({
      id: "flow",
      name: title || "",
      type: "mapline",
      className: "path-with-tooltip",
      animation: false,
      // color:
      // "#" +
      // Math.floor(Math.random() * 16777215)
      // .toString(16)
      // .slice(0, 3),
      // color: eColors[0],
      color: "#ffffff00",
      tooltip: {
        className: "map-tooltip",
        headerFormat: "",
        useHTML: true,
        pointFormatter: function () {
          return this.id;
        }
      },
      data: toArray
        .map((to) => {
          return chartObj.get(to.country) && fromPoint
            ? {
                // id: `Thi is the Data: ${from} -> ${to.toCountry}`,
                id: `<div style="display:flex; align-items:center; width:100%; margin: 10px 10px 0px 10px; z-index: 999 !important">
                <img alt="country flag"  src="https://hatscripts.github.io/circle-flags/flags/${
                  from !== null && from.toLowerCase()
                }.svg" style="height: 24px; width: 24px; object-fit:contain"> 
                <strong style="color: ${
                  eColors[0]
                }; font-size: 18px; margin-left: 8px">  ${
                  countries[from].name
                }</strong>
                <svg width="20" height="20"><path d="m12 4-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"></path></svg>
                <img alt="country flag" src="https://hatscripts.github.io/circle-flags/flags/${
                  to.country !== null && to.country.toLowerCase()
                }.svg" style="height: 24px; width: 24px;  object-fit:contain; margin-left: 5px"> <strong style="color: ${
                  eColors[0]
                }; font-size: 18px; margin-left: 8px">  ${
                  countries[to.country].name
                }</strong>

                 </div>
              <ul style="list-style-type: none; margin-left:-38px; font-size: 14px;">
                <li style="display:flex; justify-content:space-between; align-items:center;">
                <div style=" margin: 5px 10px 10px 10px">
                  <strong>
                    ${messages[lang]["totalRevenue"]}:
                  </strong>
                  <br/>
                  ${formatNumber(to.total_revenue, 0) + currency.symbol}
                  </div>
                  <div style="margin: ${
                    lang === "de" ? "5px 22px 10px 20px" : "5px 30px 10px 20px"
                  }">
                  <strong>
                  ${messages[lang]["totalOrderValue"]}:
                </strong>
                <br/>
                 ${formatNumber(to.invoice_count, 0)}
                  </div>
                </li>
                <li style="display:flex; justify-content:space-between; align-items:center">
                <div style=" margin: 5px 10px 10px 10px">
                  <strong>
                  ${messages[lang]["averageOrderValue"]}:
                  </strong>
                  <br/>
                  ${formatNumber(to.average_revenue, 0) + currency.symbol}
                  </div>
                  <div style=" margin: 5px 30px 10px 20px">
                  <strong>
                  ${messages[lang]["totalCreditNotes"]}:
                </strong>
                <br/>
                ${formatNumber(to.total_credits, 0) + currency.symbol}
                  </div>
                </li>
              </ul> `,
                path: pointsToPath(fromPoint, chartObj.get(to.country), true),
                lineWidth: 20
              }
            : undefined;
        })
        .filter((item) => item !== undefined),
      events: {
        show: function () {
          // this.renderer.definition({
          //   tagName: "marker",
          //   id: "markerArrow",
          //   refY: 5,
          //   refX: 9,
          //   markerWidth: 5,
          //   markerHeight: 5,
          //   orient: "auto",
          //   children: [
          //     {
          //       tagName: "path",
          //       d: "M 0 0 L 10 5 L 0 10 Z",
          //       fill: eColors[1],
          //     },
          //   ],
          // });
        }
      }
    });
  }

  function addHeat() {
    chartObj.addSeries({
      id: "heat",
      borderColor: chartColors.borderColor,
      borderWidth: 2,
      name: "Countries Data",
      data: mapData.map((country) => {
        return {
          code: country.country,
          averageOrderValueActualYear: country.average_revenue,
          totalCreditNotesActualYear: country.total_credits,
          totalOrdersActualYear: country.invoice_count,
          totalRevenueActualYear: country.total_revenue,
          value: country.total_revenue
        };
      }),
      tooltip: {
        className: "map-tooltip",
        headerFormat: "",
        useHTML: true,
        pointFormatter: function () {
          return `<div style="display:flex; align-items:center; width:100%; margin: 10px 10px 0px 10px; z-index: 999 !important"> <img alt="country flag" src="https://hatscripts.github.io/circle-flags/flags/${
            this.options.code !== null && this.options.code.toLowerCase()
          }.svg" style="height: 24px; width: 24px;"> <strong style="color: ${
            eColors[0]
          }; font-size: 18px; margin-left: 8px">${this.name} :</strong> </div>
        <ul style="list-style-type: none; margin-left:-38px; font-size: 14px;">
          <li style="display:flex; justify-content:space-between; align-items:center;">
          <div style=" margin: 5px 10px 10px 10px">
            <strong>
              ${messages[lang]["totalRevenue"]}:
            </strong>
            <br/>
            ${formatNumber(this.totalRevenueActualYear, 0) + currency.symbol}
            </div>
            <div style="margin: ${
              lang === "de" ? "5px 22px 10px 20px" : "5px 30px 10px 20px"
            }">
            <strong>
            ${messages[lang]["totalOrderValue"]}:
          </strong>
          <br/>
           ${formatNumber(this.totalOrdersActualYear, 0)}
            </div>
          </li>
          <li style="display:flex; justify-content:space-between; align-items:center">
          <div style=" margin: 5px 10px 10px 10px">
            <strong>
            ${messages[lang]["averageOrderValue"]}:
            </strong>
            <br/>
            ${
              formatNumber(this.averageOrderValueActualYear, 0) +
              currency.symbol
            }
            </div>
            <div style=" margin: 5px 30px 10px 20px">
            <strong>
            ${messages[lang]["totalCreditNotes"]}:
          </strong>
          <br/>
          ${formatNumber(this.totalCreditNotesActualYear, 0) + currency.symbol}
            </div>
          </li>
        </ul> `;
        },

        borderWidth: 0,
        borderRadius: 14,
        backgroundColor: "#ebebeb"
      },
      dataLabels: {
        enabled: true,
        useHTML: true,
        formatter() {
          if (this.point.value) {
            return `<img src="https://hatscripts.github.io/circle-flags/flags/${
              this.point.properties && this.point.properties["hc-key"]
            }.svg" aria-label="country flag" style="height: 25px; width: 25px; border:4px solid #ebebeb; border-radius: 100px; ">`;
          }
        }
      }
    });
  }

  function addTax() {
    chartObj.addSeries({
      id: "tax",
      name: "Countries Data",
      // data: taxData.map((country) => {
      //   return {
      //     code: country.fromCountry,
      //     grossRevenue: Math.abs(country.totalRevenue + country.totalVat),
      //     netRevenue: Math.abs(country.totalRevenue),
      //     vatAmount: Math.abs(country.totalVat),
      //     value: Math.abs(country.totalVat),
      //   };
      // }),
      borderColor: chartColors.borderColor,
      borderWidth: 2,
      data: taxData.map((country) => {
        return {
          code: country.country,
          grossRevenue: country.total_invoice,
          netRevenue: country.total_revenue,
          vatAmount: country.total_VAT,
          value: country.total_VAT
        };
      }),
      // data: [],
      tooltip: {
        headerFormat: "",
        useHTML: true,
        pointFormatter: function () {
          return `<div style="display:flex; align-items:center; width:100%; margin: 10px 10px 0px 10px"> <img alt="country flag" src="https://hatscripts.github.io/circle-flags/flags/${
            this.options.code !== null && this.options.code.toLowerCase()
          }.svg" style="height: 24px; width: 24px"> <strong style="color: ${
            eColors[0]
          }; font-size: 18px; margin-left: 8px">${this.name} :</strong> </div> 
                                <ul style="list-style-type: none; margin-left:-38px; font-size:14px">
                                <li style="display:flex; justify-content:space-between; align-items:center;">
          <div style=" margin: 5px 10px 10px 10px">
            <strong>
            ${messages[lang]["grossRevenue"]}:
            </strong>
            <br/>
            ${formatNumber(this.grossRevenue, 0) + currency.symbol}
            </div>
            <div style=" margin: 5px 40px 10px 20px">
            <strong>
            ${messages[lang]["netRevenue"]}:
          </strong>
          <br/>
           ${formatNumber(this.netRevenue, 0) + currency.symbol}
            </div>
          </li>

          <li style="display:flex; justify-content:space-between; align-items:center;">
          <div style=" margin: 5px 10px 10px 10px">
            <strong>
            ${messages[lang]["vatAmount"]}:
            </strong>
            <br/>
            ${formatNumber(this.vatAmount, 0) + currency.symbol}
            </div>
            <div style=" margin: 5px 40px 10px 20px">
       
            </div>
          </li>`;
        },

        borderWidth: 0,
        borderRadius: 14,
        backgroundColor: "#ebebeb"
      },
      dataLabels: {
        enabled: true,
        // useHTML: true,
        formatter() {
          if (this.point.value !== null) {
            return `<img alt="country flag" src="https://hatscripts.github.io/circle-flags/flags/${
              this.point.properties && this.point.properties["hc-key"]
            }.svg" style="height: 25px; width: 25px; border:4px solid #ebebeb; border-radius: 100px;">`;
          }
        }
      }
    });
  }

  function formatData() {
    const data = [];

    countriesToConnect().forEach((countryCode) => {
      const code = getCountryInfo(countryCode);

      if (code) {
        data.push(code);
      }
    });

    return data;
  }

  function countriesToConnect() {
    const countries = [];

    fromToData.forEach((item) => {
      countries.push(item.country);
      item.heatMapReportDtos.forEach((country) => {
        countries.push(country.country);
      });
    });

    return [...new Set(countries)];
  }

  function getCountryInfo(countryCode) {
    return countriesCords.find((country) => countryCode === country.code);
  }

  const updateChart = (chart) => {
    setChartObj(chart);
  };

  return (
    <HighchartsReact
      constructorType={"mapChart"}
      highcharts={Highcharts}
      options={mapOptions}
      callback={updateChart}
      ref={chartContainerRef}
    />
  );
}
